import { cn } from '@src/design-system/helpers';
import { Text } from '@src/design-system/text';
import { ReminderChannelCell } from '@src/features/reminders/components/all-reminders/shared/reminder-channel-cell';
import { messageContentToPreviewText } from '@shared/features/reminders/helpers/use-template-channel-content-drafts';
import type { TMedia } from '@src/shared/types/media';
import { FileCheck } from 'lucide-react';

export type MessagePreviewProps = {
  className?: string;
  channel?: string;
  content?: string;
  attachments?: TMedia[];
};

function formatFileSize(bytes: number) {
  if (!Number.isFinite(bytes) || bytes <= 0) return '—';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb % 1 === 0 ? mb.toFixed(0) : mb.toFixed(1)} MB`;
  const kb = bytes / 1024;
  return `${Math.max(1, Math.round(kb))} KB`;
}

export function MessagePreview({
  className,
  channel,
  content,
  attachments = [],
}: MessagePreviewProps) {
  const plainText = messageContentToPreviewText(content);

  return (
    <div className={cn('flex w-full flex-col gap-3', className)}>
      {channel && (
        <section className='rounded-[12px] border border-primary-200 bg-white p-3'>
          <p className='mb-1 text-xs font-medium text-gray-600'>Channel</p>
          <ReminderChannelCell channel={channel} />
        </section>
      )}

      {plainText && (
        <section className='min-h-[120px] flex-1 rounded-[12px] border border-primary-200 bg-white p-3'>
          <p className='whitespace-pre-wrap text-sm leading-relaxed text-gray-800'>
            {plainText}
          </p>
        </section>
      )}

      {attachments.length > 0 ? (
        <section className='flex flex-col gap-2'>
          {attachments.map((attachment) => (
            <div
              key={attachment.id}
              className='flex items-center gap-1 rounded-[12px] border border-primary-200 bg-white p-3'
            >
              <FileCheck
                size={40}
                color='#fff'
                fill='#584777'
              />
              <div className='min-w-0'>
                <button
                  type='button'
                  className='block max-w-full truncate text-left text-sm font-medium text-gray-900 hover:underline'
                  onClick={() => window.open(attachment.file, '_blank')}
                >
                  {attachment.name || 'Attachment'}
                </button>
                <Text className='text-xs text-gray-500'>
                  {formatFileSize(attachment.size)}
                </Text>
              </div>
            </div>
          ))}
        </section>
      ) : null}
    </div>
  );
}
